import { FunctionComponent } from 'preact';
import { useState, useEffect, useMemo, useCallback } from 'preact/hooks';
import { HTMLAttributes } from 'preact/compat';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  Field,
  Input,
  TextArea,
  Picker,
  PickerOption,
} from '@adobe-commerce/elsie/components';
import { classes } from '@adobe-commerce/elsie/lib';
import { Option, OptionField } from '@/pdp/data/models/values-model';
import './GiftCardOptions.css';

enum ComponentType {
  INPUT = 'input',
  TEXTAREA = 'textarea',
  PICKER = 'picker',
}

export interface GiftCardOptionsProps extends HTMLAttributes<HTMLDivElement> {
  options: Option[];
  currency?: string;
  optionValues: OptionField[];
  onOptionChange: (uid: string, value: string) => void;
  onValidationChange: (valid: boolean) => void;
}

// Utility functions
const getInputType = (validationType: string): string => {
  switch (validationType) {
    case 'number':
      return 'number';
    case 'email':
      return 'email';
    default:
      return 'text';
  }
};

const getComponentType = (option: Option): ComponentType => {
  if (option.name === 'giftcard/giftcard_message') {
    return ComponentType.TEXTAREA;
  }

  if (option.name === 'giftcard/giftcard_amount') {
    return ComponentType.PICKER;
  }

  return ComponentType.INPUT;
};

const mapToPickerOptions = (
  values: (string | number)[],
  hasCustomAmountField: boolean,
  chooseAmountText: string,
  otherAmountText: string
): PickerOption[] => {
  const options: PickerOption[] = [
    {
      text: chooseAmountText,
      value: '0',
      disabled: false,
    },
    ...values.map((value) => ({
      text: value.toString(),
      value: value.toString(),
      disabled: false,
    })),
    ...(hasCustomAmountField
      ? [
          {
            text: otherAmountText,
            value: 'other',
            disabled: false,
          },
        ]
      : []),
  ];
  return options;
};

const validate = (
  option: Option,
  value: string,
  errorMessages: {
    RequiredFieldError: string;
    NumberError: string;
    MinError: string;
    MaxError: string;
    MinLengthError: string;
    MaxLengthError: string;
    EmailError: string;
    InvalidValueError: string;
  }
) => {
  const { validationRule } = option;

  if (option.name === 'giftcard/giftcard_amount') {
    if (
      validationRule.required &&
      (value === '0' || !value || value.trim() === '')
    ) {
      return errorMessages.RequiredFieldError;
    }
    if (value === '0') {
      return errorMessages.RequiredFieldError;
    }
    if (value === 'other') {
      return '';
    }
  } else if (validationRule.required && (!value || value.trim() === '')) {
    return errorMessages.RequiredFieldError;
  }

  if (!value || value.trim() === '') {
    return '';
  }

  switch (validationRule.type) {
    case 'email': {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return errorMessages.EmailError;
      }
      break;
    }

    case 'number': {
      const num = Number(value);
      if (isNaN(num)) {
        return errorMessages.NumberError;
      }

      // Check min value
      if (validationRule.min !== undefined && num < validationRule.min) {
        return errorMessages.MinError.replace(
          '{min}',
          validationRule.min.toString()
        );
      }

      // Check max value
      if (validationRule.max !== undefined && num > validationRule.max) {
        return errorMessages.MaxError.replace(
          '{max}',
          validationRule.max.toString()
        );
      }

      // Check allowed values
      if (validationRule.values) {
        const isValidValue = validationRule.values.some((val) =>
          typeof val === 'number' ? val === num : val === value
        );
        if (!isValidValue) {
          return errorMessages.InvalidValueError.replace(
            '{values}',
            validationRule.values.join(', ')
          );
        }
      }
      break;
    }

    case 'string': {
      const length = value.length;

      // Check min length
      if (
        validationRule.minLength !== undefined &&
        length < validationRule.minLength
      ) {
        return errorMessages.MinLengthError.replace(
          '{min_length}',
          validationRule.minLength.toString()
        );
      }

      // Check max length
      if (
        validationRule.maxLength !== undefined &&
        length > validationRule.maxLength
      ) {
        return errorMessages.MaxLengthError.replace(
          '{max_length}',
          validationRule.maxLength.toString()
        );
      }
      break;
    }
  }

  return '';
};

export const GiftCardOptions: FunctionComponent<GiftCardOptionsProps> = ({
  options,
  currency,
  optionValues,
  onOptionChange,
  onValidationChange,
}) => {
  const ChooseAmount = useText(
    'PDP.Product.GiftCardOptions.ChooseAmount.label'
  ).label;
  const OtherAmount = useText(
    'PDP.Product.GiftCardOptions.OtherAmount.label'
  ).label;
  const RequiredFieldError = useText(
    'PDP.Product.GiftCardOptions.RequiredFieldError.label'
  ).label;
  const NumberError = useText(
    'PDP.Product.GiftCardOptions.NumberError.label'
  ).label;
  const MinError = useText('PDP.Product.GiftCardOptions.MinError.label').label;
  const MaxError = useText('PDP.Product.GiftCardOptions.MaxError.label').label;
  const MinLengthError = useText(
    'PDP.Product.GiftCardOptions.MinLengthError.label'
  ).label;
  const MaxLengthError = useText(
    'PDP.Product.GiftCardOptions.MaxLengthError.label'
  ).label;
  const EmailError = useText(
    'PDP.Product.GiftCardOptions.EmailError.label'
  ).label;
  const InvalidValueError = useText(
    'PDP.Product.GiftCardOptions.InvalidValueError.label'
  ).label;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCustomAmount, setShowCustomAmount] = useState(false);

  // Helper function to find option by name
  const findOptionByName = useCallback(
    (name: string): Option | undefined => {
      return options.find((option) => option.name === name);
    },
    [options]
  );

  // Memoize error messages to avoid recreating them on every render
  const errorMessages = useMemo(
    () => ({
      RequiredFieldError,
      NumberError,
      MinError,
      MaxError,
      MinLengthError,
      MaxLengthError,
      EmailError,
      InvalidValueError,
    }),
    [
      RequiredFieldError,
      NumberError,
      MinError,
      MaxError,
      MinLengthError,
      MaxLengthError,
      EmailError,
      InvalidValueError,
    ]
  );

  // Run initial validation only when options or optionValues change
  useEffect(() => {
    const valid = options.every((option) => {
      // Skip validation for custom gift card amount when it's not shown
      if (
        option.name === 'giftcard/custom_giftcard_amount' &&
        !showCustomAmount
      ) {
        return true;
      }

      const field = optionValues.find((f) => f.id === option.uid);
      const value = field?.value || '';
      return validate(option, value, errorMessages) === '';
    });

    onValidationChange(valid);
  }, [
    options,
    optionValues,
    onValidationChange,
    errorMessages,
    showCustomAmount,
  ]);

  useEffect(() => {
    const giftCardAmountOption = findOptionByName('giftcard/giftcard_amount');
    const customAmountOption = findOptionByName(
      'giftcard/custom_giftcard_amount'
    );

    const giftCardAmountField = optionValues.find(
      (f) => f.id === giftCardAmountOption?.uid
    );

    const selectedValue = giftCardAmountField?.value || '';
    const wasShowingCustomAmount = showCustomAmount;

    // If there's no gift card amount option, always show custom amount
    // Otherwise, show custom amount only when "other" is selected
    const willShowCustomAmount = giftCardAmountOption
      ? selectedValue === 'other'
      : !!customAmountOption;

    setShowCustomAmount(willShowCustomAmount);

    // Reset custom amount value when switching away from "other"
    if (wasShowingCustomAmount && !willShowCustomAmount && customAmountOption) {
      onOptionChange(customAmountOption.uid, '');
    }
  }, [optionValues, findOptionByName, showCustomAmount, onOptionChange]);

  const handleChange = (option: Option, value: string) => {
    const error = validate(option, value, errorMessages);
    setErrors((prev: Record<string, string>) => ({
      ...prev,
      [option.uid]: error,
    }));

    onOptionChange(option.uid, value);
  };

  const handleBlur = (option: Option, value: string) => {
    const error = validate(option, value, errorMessages);
    setErrors((prev: Record<string, string>) => ({
      ...prev,
      [option.uid]: error,
    }));
  };

  const handlePickerSelect = (option: Option, event: any) => {
    const selectedValue = event.target.value;
    handleChange(option, selectedValue);
  };

  const renderField = (option: Option) => {
    const field = optionValues.find((f) => f.id === option.uid);
    const value = field?.value;
    const error = errors[option.uid];
    const componentType = getComponentType(option);
    const hasCustomAmountField = !!findOptionByName(
      'giftcard/custom_giftcard_amount'
    );

    // Add currency to custom gift card amount label
    let label = option.label;
    if (option.name === 'giftcard/custom_giftcard_amount' && currency) {
      label = `${option.label} ${currency}`;
    }

    return (
      <Field
        key={option.uid}
        label={label}
        className={classes([
          'pdp-gift-card-options__option-field',
          componentType === ComponentType.TEXTAREA &&
            'pdp-gift-card-options__option-field--full-width',
        ])}
        error={error}
      >
        {(() => {
          if (componentType === ComponentType.TEXTAREA) {
            return (
              <TextArea
                value={value}
                label={option.label}
                onChange={(e: any) =>
                  handleChange(option, (e.target as HTMLTextAreaElement).value)
                }
                onBlur={(e: any) =>
                  handleBlur(option, (e.target as HTMLTextAreaElement).value)
                }
                aria-invalid={!!error}
              />
            );
          }

          if (componentType === ComponentType.PICKER) {
            return (
              <Picker
                aria-label={option.label}
                label={option.label}
                handleSelect={(event) => handlePickerSelect(option, event)}
                options={mapToPickerOptions(
                  option.validationRule.values || [],
                  hasCustomAmountField,
                  ChooseAmount,
                  OtherAmount
                )}
                value={value}
                aria-invalid={!!error}
              />
            );
          }

          return (
            <Input
              type={getInputType(option.validationRule.type)}
              value={value}
              onChange={(e) =>
                handleChange(option, (e.target as HTMLInputElement).value)
              }
              onBlur={(e) =>
                handleBlur(option, (e.target as HTMLInputElement).value)
              }
              aria-invalid={!!error}
            />
          );
        })()}
      </Field>
    );
  };

  return (
    <div className="pdp-gift-card-options__grid">
      {/* Row 1: Gift card amount and custom amount */}
      <div className="pdp-gift-card-options__row">
        {findOptionByName('giftcard/giftcard_amount') &&
          renderField(findOptionByName('giftcard/giftcard_amount')!)}
        {findOptionByName('giftcard/custom_giftcard_amount') &&
          showCustomAmount &&
          renderField(findOptionByName('giftcard/custom_giftcard_amount')!)}
      </div>

      {/* Row 2: Sender name and email */}
      <div className="pdp-gift-card-options__row">
        {findOptionByName('giftcard/giftcard_sender_name') &&
          renderField(findOptionByName('giftcard/giftcard_sender_name')!)}
        {findOptionByName('giftcard/giftcard_sender_email') &&
          renderField(findOptionByName('giftcard/giftcard_sender_email')!)}
      </div>

      {/* Row 3: Recipient name and email */}
      <div className="pdp-gift-card-options__row">
        {findOptionByName('giftcard/giftcard_recipient_name') &&
          renderField(findOptionByName('giftcard/giftcard_recipient_name')!)}
        {findOptionByName('giftcard/giftcard_recipient_email') &&
          renderField(findOptionByName('giftcard/giftcard_recipient_email')!)}
      </div>

      {/* Row 4: Gift card message */}
      <div className="pdp-gift-card-options__row">
        {findOptionByName('giftcard/giftcard_message') &&
          renderField(findOptionByName('giftcard/giftcard_message')!)}
      </div>
    </div>
  );
};
